import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { clearCart } from '../../store/slices/cartSlice';
import { addNotification } from '../../store/slices/uiSlice';
import './Checkout.css';
import { DynamicIcon } from '../../components/common/SvgIcons';

const Checkout: React.FC = () => {
  const { items, total, subtotal } = useSelector((state: RootState) => state.cart);
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    address: '',
    city: '',
    zipCode: '',
  });

  // Sandbox Gateway States
  const [isSandboxOpen, setIsSandboxOpen] = useState(false);
  const [sandboxTab, setSandboxTab] = useState<'card' | 'upi' | 'netbanking' | 'po'>('card');
  const [sandboxError, setSandboxError] = useState('');
  const [sandboxSuccess, setSandboxSuccess] = useState(false);
  const [cardDetails, setCardDetails] = useState({ number: '', expiry: '', cvv: '', name: '' });
  const [upiDetails, setUpiDetails] = useState({ vpa: '' });
  const [selectedBank, setSelectedBank] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOpenSandbox = (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) {
      dispatch(addNotification({ type: 'warning', title: 'Warning', message: 'Your cart is empty!' }));
      return;
    }
    
    // Check basic billing validity
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.address.trim() || !formData.city.trim() || !formData.zipCode.trim()) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Please fill out all Billing & Delivery address details to proceed to secure payment.' }));
      return;
    }

    setSandboxError('');
    setSandboxSuccess(false);
    setIsSandboxOpen(true);
  };

  const handleSimulateSuccess = () => {
    setSandboxSuccess(true);
    setSandboxError('');

    // Create the persistent order
    const newOrder = {
      orderId: `ORD_${Math.floor(100000 + Math.random() * 900000)}`,
      date: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
      items: items.map(item => ({
        id: item.productId,
        name: item.product.name,
        price: item.product.discountedPrice || item.product.price,
        quantity: item.quantity,
        image: item.product.image || "/assets/images/camera.png"
      })),
      total: total,
      status: 'Processing',
      shippingDetails: formData,
      userEmail: user ? user.email : 'guest@industrybull.com'
    };

    // Save into live user orders localStorage database
    try {
      const existingStr = localStorage.getItem('user_orders');
      const existingList = existingStr ? JSON.parse(existingStr) : [];
      localStorage.setItem('user_orders', JSON.stringify([newOrder, ...existingList]));
    } catch (err) {
      console.error('Failed to save order record in sandbox', err);
    }

    // Delay redirect to let success animation play
    setTimeout(() => {
      dispatch(clearCart());
      setIsSandboxOpen(false);
      navigate('/account/orders');
    }, 2000);
  };

  const handleSimulateFailure = () => {
    setSandboxSuccess(false);
    setSandboxError('TRANSACTION DECLINED: Payment failed. Please verify your payment details and try again.');
  };

  return (
    <div className="checkout-page">
      <h1>Checkout</h1>
      <div className="checkout-container">
        
        <div className="checkout-form">
          <h2>Billing & Delivery Details</h2>
          <form onSubmit={handleOpenSandbox}>
            <div className="form-row">
              <div className="form-group">
                <label>First Name *</label>
                <input required type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="e.g. John" />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input required type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="e.g. Doe" />
              </div>
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input required type="email" name="email" value={formData.email} onChange={handleChange} placeholder="e.g. buyer@business.com" />
            </div>
            <div className="form-group">
              <label>Street Address / Warehouse Address *</label>
              <input required type="text" name="address" value={formData.address} onChange={handleChange} placeholder="e.g. Plot No 42, Industrial Area Phase 1" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Town / City *</label>
                <input required type="text" name="city" value={formData.city} onChange={handleChange} placeholder="e.g. New Delhi" />
              </div>
              <div className="form-group">
                <label>Postcode / ZIP *</label>
                <input required type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} placeholder="e.g. 110020" />
              </div>
            </div>
            
            <button type="submit" className="pay-btn" style={{ background: '#df2027' }}>
              PROCEED TO SECURE PAYMENT
            </button>
          </form>
        </div>

        <div className="checkout-summary">
          <h2>Order Summary</h2>
          <div className="checkout-items">
            {items.map((item) => (
              <div key={item.id} className="checkout-item">
                <span className="checkout-item-name" style={{ fontWeight: '500' }}>{item.product.name} (x{item.quantity})</span>
                <span style={{ fontWeight: '600' }}>₹{(item.product.discountedPrice || item.product.price) * item.quantity}</span>
              </div>
            ))}
            {items.length === 0 && <p style={{color: '#888'}}>No items in cart.</p>}
          </div>
          
          <div className="summary-item">
            <span>Subtotal</span>
            <span>₹{subtotal.toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span>Shipping / Delivery</span>
            <span>{subtotal > 1000 ? 'Free Shipping' : '₹50.00'}</span>
          </div>
          <div className="summary-item">
            <span>Tax (18% GST)</span>
            <span>₹{(subtotal * 0.18).toFixed(2)}</span>
          </div>
          <div className="summary-item total">
            <span>Total Payable Amount</span>
            <span style={{ color: '#df2027', fontWeight: '800' }}>₹{total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* ── SECURE SANDBOX PAYMENT GATEWAY MODAL ── */}
      {isSandboxOpen && (
        <div className="payment-sandbox-backdrop" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div className="payment-sandbox-modal" style={{
            width: '100%',
            maxWidth: '560px',
            background: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 1px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            fontFamily: 'system-ui, sans-serif',
            animation: 'fadeInScale 0.3s ease-out'
          }}>
            {/* Header Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #df2027, #b91c1c)',
              color: '#ffffff',
              padding: '24px',
              position: 'relative'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', letterSpacing: '0.5px' }}>INDUSTRYBULL SECURE GATEWAY</h3>
                  <div style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.85, display: 'flex', alignItems: 'center', gap: '4px' }}><DynamicIcon name="lock" size={12} stroke="#ffffff" /> <span>256-Bit SSL Encrypted Payment Gateway</span></div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '10px', textTransform: 'uppercase', opacity: 0.75 }}>Payable Amount</div>
                  <div style={{ fontSize: '20px', fontWeight: '800' }}>₹{total.toFixed(2)}</div>
                </div>
              </div>
            </div>

            {/* Success Overlay */}
            {sandboxSuccess ? (
              <div style={{ padding: '60px 40px', textAlign: 'center', background: '#ffffff', minHeight: '340px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: '#dcfce7',
                  color: '#15803d',
                  fontSize: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  boxShadow: '0 4px 10px rgba(21, 128, 61, 0.15)'
                }}><DynamicIcon name="check" size={32} stroke="#15803d" /></div>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#16a34a', margin: '0 0 8px 0' }}>PAYMENT SUCCESSFUL!</h3>
                <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px 0', lineHeight: '1.5' }}>Your B2B commercial procurement transaction has been approved securely. Redirecting to your Orders dashboard...</p>
                <div className="spinner" style={{ width: '24px', height: '24px', border: '3px solid #10b981', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
              <div>
                {/* Error Banner */}
                {sandboxError && (
                  <div style={{ margin: '16px 24px 0 24px', background: '#fef2f2', border: '1px solid #fca5a5', padding: '12px 16px', borderRadius: '8px', color: '#b91c1c', fontSize: '13px', lineHeight: '1.4', fontWeight: '600' }}>
                    {sandboxError}
                  </div>
                )}

                {/* Navigation Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  <button onClick={() => setSandboxTab('card')} style={{ flex: 1, padding: '14px 10px', border: 'none', background: 'none', borderBottom: sandboxTab === 'card' ? '3px solid #df2027' : '3px solid transparent', color: sandboxTab === 'card' ? '#df2027' : '#64748b', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}><DynamicIcon name="card" size={14} /> Card</button>
                  <button onClick={() => setSandboxTab('upi')} style={{ flex: 1, padding: '14px 10px', border: 'none', background: 'none', borderBottom: sandboxTab === 'upi' ? '3px solid #df2027' : '3px solid transparent', color: sandboxTab === 'upi' ? '#df2027' : '#64748b', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}><DynamicIcon name="upi" size={14} /> UPI</button>
                  <button onClick={() => setSandboxTab('netbanking')} style={{ flex: 1, padding: '14px 10px', border: 'none', background: 'none', borderBottom: sandboxTab === 'netbanking' ? '3px solid #df2027' : '3px solid transparent', color: sandboxTab === 'netbanking' ? '#df2027' : '#64748b', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}><DynamicIcon name="bank" size={14} /> Net Banking</button>
                  <button onClick={() => setSandboxTab('po')} style={{ flex: 1, padding: '14px 10px', border: 'none', background: 'none', borderBottom: sandboxTab === 'po' ? '3px solid #df2027' : '3px solid transparent', color: sandboxTab === 'po' ? '#df2027' : '#64748b', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}><DynamicIcon name="invoice" size={14} /> B2B Invoice</button>
                </div>

                {/* Tab Contents */}
                <div style={{ padding: '24px', minHeight: '220px' }}>
                  {sandboxTab === 'card' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Mock Card Number</label>
                        <input type="text" placeholder="4111 2222 3333 4444" value={cardDetails.number} onChange={e => setCardDetails({...cardDetails, number: e.target.value.replace(/\D/g, '')})} style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }} />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Expiry Date</label>
                          <input type="text" placeholder="MM/YY" value={cardDetails.expiry} onChange={e => setCardDetails({...cardDetails, expiry: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }} />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>CVV Code</label>
                          <input type="password" placeholder="***" maxLength={3} value={cardDetails.cvv} onChange={e => setCardDetails({...cardDetails, cvv: e.target.value.replace(/\D/g, '')})} style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }} />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Cardholder Name</label>
                        <input type="text" placeholder="John B2B Enterprise" value={cardDetails.name} onChange={e => setCardDetails({...cardDetails, name: e.target.value})} style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }} />
                      </div>
                    </div>
                  )}

                  {sandboxTab === 'upi' && (
                    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                      <div style={{ border: '1px solid #cbd5e1', borderRadius: '12px', padding: '10px', display: 'inline-block', background: '#fff' }}>
                        {/* Interactive Simulated Scan code */}
                        <div style={{ width: '120px', height: '120px', background: '#eaeaea', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                          <span style={{ fontSize: '10px', color: '#475569', fontWeight: 'bold' }}>[ MOCK QR CODE ]</span>
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, border: '4px solid #df2027', opacity: 0.15 }}></div>
                        </div>
                      </div>
                      <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>Scan this mock QR code from any bank UPI app, or type your Virtual Payment Address (VPA) below:</p>
                      <input type="text" placeholder="e.g. buyer@okaxis" value={upiDetails.vpa} onChange={e => setUpiDetails({ vpa: e.target.value })} style={{ width: '80%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', textAlign: 'center' }} />
                    </div>
                  )}

                  {sandboxTab === 'netbanking' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '12px' }}>Choose a Commercial B2B Corporate Bank</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        {['State Bank of India', 'HDFC Corporate Bank', 'ICICI Commercial Bank', 'Axis Bank Corporate', 'Punjab National Bank', 'Kotak Mahindra Bank'].map(bank => (
                          <button key={bank} onClick={() => setSelectedBank(bank)} style={{
                            padding: '12px',
                            border: selectedBank === bank ? '2px solid #df2027' : '1px solid #cbd5e1',
                            borderRadius: '8px',
                            background: selectedBank === bank ? '#fef2f2' : '#ffffff',
                            color: '#334155',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'all 0.15s ease'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><DynamicIcon name="bank" size={14} /> {bank}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {sandboxTab === 'po' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px' }}>
                        <h4 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>Corporate Line of Credit</h4>
                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>Use your company's pre-approved procurement account limits to pay. A proforma invoice will be delivered to your registered business email for approval.</p>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Purchase Order (P.O.) Number</label>
                        <input type="text" placeholder="e.g. PO-9988221" style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '5px' }}>Company GSTIN Number</label>
                        <input type="text" placeholder="07AAAAA1111A1Z1" style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Simulator Buttons Footer */}
                <div style={{
                  background: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  padding: '20px 24px',
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button onClick={() => setIsSandboxOpen(false)} style={{
                    padding: '10px 16px',
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    color: '#475569',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}>
                    Cancel Transaction
                  </button>
                  <button onClick={handleSimulateFailure} style={{
                    padding: '10px 16px',
                    background: '#ef4444',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}>
                    Simulate Failure
                  </button>
                  <button onClick={handleSimulateSuccess} style={{
                    padding: '10px 20px',
                    background: '#10b981',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
                  }}>
                    Simulate Success
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Dynamic Keyframes injected globally for spinner & scale animation */}
      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Checkout;
